// Composant SendEmailModal amélioré

import React, { useState, useEffect, useContext, useMemo } from "react";
import { ApiContext } from '../App';
import { toast } from "react-toastify";
import { X, Minimize2, Maximize2, Trash2, Plus, Search } from "lucide-react";
import RichTextEditor from "./common/RichTextEditor";
import { getFirstEmail, getAllEmails } from '../utils/emailHelpers';

const SendEmailModal = ({ recipients, threadInfo = null, onClose, onSend, defaultEmailType = 'professional', initialMessage = "" }) => {
  const API = useContext(ApiContext);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState(initialMessage);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const [showFieldsDropdown, setShowFieldsDropdown] = useState(false);
  const quillRef = React.useRef(null);

  // Nouveaux états pour gérer les destinataires enrichis
  const [recipientList, setRecipientList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [bccList, setBccList] = useState([]);

  const [attachments, setAttachments] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [selectedTagsFilter, setSelectedTagsFilter] = useState([]);

  // États pour la recherche
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [allIndividuals, setAllIndividuals] = useState([]);

  // Charger les individuals au démarrage
  useEffect(() => {
    const fetchIndividuals = async () => {
      try {
        const response = await API.get("/api/individuals");
        setAllIndividuals(response.data);
      } catch (error) {
        console.error("Error fetching individuals:", error);
      }
    };
    fetchIndividuals();
  }, [API]);

  // Charger les informations des destinataires initiaux
  useEffect(() => {
    const fetchRecipients = async () => {
      if (!recipients || recipients.length === 0) return;

      try {
        const individuals = await Promise.all(
          recipients.map(id => API.get(`/api/individuals/${id}`))
        );

        const enrichedRecipients = individuals.map(res => {
          // Récupérer tous les emails disponibles
          const profEmails = getAllEmails(res.data.professionalEmail);
          const persEmails = getAllEmails(res.data.personalEmail);
          const allEmails = [
            ...profEmails.map(email => ({ email, type: 'professional' })),
            ...persEmails.map(email => ({ email, type: 'personal' }))
          ];

          // Sélectionner le premier email du type par défaut si disponible
          let selectedEmail = null;
          if (defaultEmailType === 'professional' && profEmails.length > 0) {
            selectedEmail = profEmails[0];
          } else if (defaultEmailType === 'personal' && persEmails.length > 0) {
            selectedEmail = persEmails[0];
          } else if (allEmails.length > 0) {
            selectedEmail = allEmails[0].email;
          }

          return {
            individualId: res.data._id,
            name: res.data.Name,
            professionalEmail: res.data.professionalEmail,
            personalEmail: res.data.personalEmail,
            allAvailableEmails: allEmails,
            selectedEmail: selectedEmail
          };
        });

        setRecipientList(enrichedRecipients);
      } catch (error) {
        console.error("Error fetching recipient details:", error);
      }
    };

    fetchRecipients();
  }, [recipients, API, defaultEmailType]);

  // Récupérer les templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await API.get("/api/templates");
        setTemplates(response.data.templates || []);
      } catch (error) {
        console.error("Error fetching email templates:", error);
      }
    };
    fetchTemplates();
  }, [API]);

  // Pré-remplir le sujet si c'est une réponse
  useEffect(() => {
    if (threadInfo?.subject) {
      // Ajouter "Re: " si pas déjà présent
      const replySubject = threadInfo.subject.startsWith('Re:')
        ? threadInfo.subject
        : `Re: ${threadInfo.subject}`;
      setSubject(replySubject);
    }
  }, [threadInfo]);

  // Fermer le dropdown des champs quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFieldsDropdown && !event.target.closest('.dynamic-fields-dropdown')) {
        setShowFieldsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFieldsDropdown]);

  // Recherche d'individuals
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const queryLower = query.toLowerCase();
    const results = allIndividuals.filter(ind => {
      // Recherche par nom
      if (ind.Name.toLowerCase().includes(queryLower)) return true;

      // Recherche dans les emails professionnels
      const profEmails = getAllEmails(ind.professionalEmail);
      if (profEmails.some(email => email.toLowerCase().includes(queryLower))) return true;

      // Recherche dans les emails personnels
      const persEmails = getAllEmails(ind.personalEmail);
      if (persEmails.some(email => email.toLowerCase().includes(queryLower))) return true;

      return false;
    }).slice(0, 10);

    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Ajouter un destinataire
  const addRecipient = (individual, type = 'to') => {
    const profEmails = getAllEmails(individual.professionalEmail);
    const persEmails = getAllEmails(individual.personalEmail);
    const allEmails = [
      ...profEmails.map(email => ({ email, type: 'professional' })),
      ...persEmails.map(email => ({ email, type: 'personal' }))
    ];

    let selectedEmail = null;
    if (defaultEmailType === 'professional' && profEmails.length > 0) {
      selectedEmail = profEmails[0];
    } else if (defaultEmailType === 'personal' && persEmails.length > 0) {
      selectedEmail = persEmails[0];
    } else if (allEmails.length > 0) {
      selectedEmail = allEmails[0].email;
    }

    const newRecipient = {
      individualId: individual._id,
      name: individual.Name,
      professionalEmail: individual.professionalEmail,
      personalEmail: individual.personalEmail,
      allAvailableEmails: allEmails,
      selectedEmail: selectedEmail
    };

    // Vérifier qu'il n'est pas déjà dans la liste
    const allLists = [...recipientList, ...ccList, ...bccList];
    if (allLists.some(r => r.individualId === individual._id)) {
      toast.warning("Cette personne est déjà dans la liste");
      return;
    }

    if (type === 'to') {
      setRecipientList([...recipientList, newRecipient]);
    } else if (type === 'cc') {
      setCcList([...ccList, newRecipient]);
    } else if (type === 'bcc') {
      setBccList([...bccList, newRecipient]);
    }

    setSearchQuery("");
    setShowSearchResults(false);
  };

  // Supprimer un destinataire
  const removeRecipient = (individualId, type = 'to') => {
    if (type === 'to') {
      setRecipientList(recipientList.filter(r => r.individualId !== individualId));
    } else if (type === 'cc') {
      setCcList(ccList.filter(r => r.individualId !== individualId));
    } else if (type === 'bcc') {
      setBccList(bccList.filter(r => r.individualId !== individualId));
    }
  };

  // Changer l'email sélectionné pour un destinataire
  const changeSelectedEmail = (individualId, type, newEmail) => {
    const updateList = (list) => list.map(r =>
      r.individualId === individualId ? { ...r, selectedEmail: newEmail } : r
    );

    if (type === 'to') {
      setRecipientList(updateList(recipientList));
    } else if (type === 'cc') {
      setCcList(updateList(ccList));
    } else if (type === 'bcc') {
      setBccList(updateList(bccList));
    }
  };

  // Gérer le changement de template
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);

    const selected = templates.find(template => template._id === templateId);
    if (selected) {
      setSubject(selected.subject);
      setMessage(selected.body);
    }
    setShowTemplateModal(false);
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filtrer les templates par recherche et tags
  const filteredTemplates = useMemo(() => {
    let result = templates;

    // Filtre par recherche
    if (templateSearchQuery.trim()) {
      const query = templateSearchQuery.toLowerCase();
      result = result.filter(template =>
        template.name.toLowerCase().includes(query) ||
        template.subject.toLowerCase().includes(query) ||
        (template.tags && template.tags.some(tag => tag.includes(query)))
      );
    }

    // Filtre par tags sélectionnés
    if (selectedTagsFilter.length > 0) {
      result = result.filter(template =>
        template.tags && selectedTagsFilter.every(filterTag =>
          template.tags.includes(filterTag)
        )
      );
    }

    return result;
  }, [templates, templateSearchQuery, selectedTagsFilter]);

  // Récupérer tous les tags uniques
  const allTags = useMemo(() => {
    const tagSet = new Set();
    templates.forEach(template => {
      if (template.tags) {
        template.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [templates]);

  // Toggle un tag dans le filtre
  const toggleTagFilter = (tag) => {
    setSelectedTagsFilter(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Insérer un champ dynamique dans le message à la position du curseur
  const insertDynamicField = (field) => {
    const placeholder = `{{${field}}}`;

    // Vérifier si on a accès à l'éditeur Quill
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();

      if (range) {
        // Insérer à la position du curseur
        quill.insertText(range.index, placeholder);
        // Déplacer le curseur après le texte inséré
        quill.setSelection(range.index + placeholder.length);
      } else {
        // Si pas de sélection, insérer à la fin
        const length = quill.getLength();
        quill.insertText(length - 1, placeholder);
        quill.setSelection(length + placeholder.length - 1);
      }
    } else {
      // Fallback : ajouter à la fin
      setMessage(prev => prev + placeholder);
    }

    setShowFieldsDropdown(false);
  };

  // Liste des champs dynamiques disponibles
  const dynamicFields = [
    { key: 'Name', label: 'Prénom', description: 'Prénom du destinataire' },
    { key: 'company', label: 'Entreprise', description: 'Entreprise du destinataire' },
    { key: 'jobTitle', label: 'Poste', description: 'Titre du poste' },
    { key: 'city', label: 'Ville', description: 'Localisation du destinataire' }
  ];

  // Envoyer l'email
  const handleSendEmail = async () => {
    if (!subject || !message) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }

    if (recipientList.length === 0) {
      toast.error("Veuillez ajouter au moins un destinataire");
      return;
    }

    setLoading(true);


    try {
      const formData = new FormData();

      formData.append("subject", subject);
      formData.append("message", message);
      formData.append("recipients", JSON.stringify(recipientList));
      formData.append("cc", JSON.stringify(ccList));
      formData.append("bcc", JSON.stringify(bccList));

      // Passer les infos de thread si c'est une réponse
      if (threadInfo) {
        formData.append("threadInfo", JSON.stringify(threadInfo));
      }

      attachments.forEach((file) => {
      formData.append("attachments", file);
    });

      await API.post("/api/individuals/send-email", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });

      toast.success("Email envoyé avec succès!");
      onSend();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Erreur lors de l'envoi de l'email");
    } finally {
      setLoading(false);
    }
  };

  // Rendu d'un destinataire
  const RecipientRow = ({ recipient, type }) => {
    const hasMultipleEmails = recipient.allAvailableEmails && recipient.allAvailableEmails.length > 1;

    return (
      <div className="flex items-center px-2 py-1 bg-indigo-100 border rounded-lg">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">{recipient.name}</p>

          {/* Sélecteur d'email */}
          {hasMultipleEmails ? (
            <select
              value={recipient.selectedEmail}
              onChange={(e) => changeSelectedEmail(recipient.individualId, type, e.target.value)}
              className="text-xs border bg-indigo-200 hover:bg-indigo-300 rounded px-1 py-0.5 max-w-[200px]"
            >
              {recipient.allAvailableEmails.map((emailObj, index) => (
                <option key={index} value={emailObj.email}>
                  {emailObj.email.length > 25 ? emailObj.email.substring(0, 25) + '...' : emailObj.email} ({emailObj.type === 'professional' ? 'Pro' : 'Perso'})
                </option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-gray-600">
              {recipient.selectedEmail}
            </span>
          )}

          {/* Bouton supprimer */}
          <button
            onClick={() => removeRecipient(recipient.individualId, type)}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Retirer"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-gray-50 fixed overflow-y-auto bottom-0 rounded-t-xl right-0 flex-1 justify-center items-center z-50 border-2 border-gray-800 shadow-lg ${
      isMinimized ? "h-[80px]" : "h-auto max-h-[90vh]"
    }`}>
      {/* Header */}
      <div className="mb-2 bg-gray-800 text-white px-6 py-2 w-[800px] max-w-[90vw] flex justify-between items-center">
        <h2 className="text-xl font-semibold m-0">
          {threadInfo ? 'Répondre' : 'Nouvel email'}
        </h2>

        <div className="flex gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-slate-200 hover:text-slate-300"
            title={isMinimized ? "Agrandir" : "Réduire"}
          >
            {isMinimized ? <Maximize2 size={20} /> : <Minimize2 size={20} />}
          </button>

          <button
            onClick={onClose}
            className="text-slate-200 hover:text-red-500"
            title="Fermer"
          >
            <X size={22} />
          </button>
        </div>
      </div>

      <div className="bg-gray-50 px-6 pb-4 w-[800px] max-w-[90vw]">
        {!isMinimized && (
          <div>
            {/* Recherche pour ajouter des destinataires */}
            <div className="mb-4 relative">
              <div className="relative pt-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Rechercher une personne..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md"
                />
              </div>

              {/* Résultats de recherche */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.map(individual => (
                    <div key={individual._id} className="p-2 hover:bg-gray-50">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{individual.Name}</p>
                          <p className="text-xs text-gray-600">
                            {getFirstEmail(individual.professionalEmail) || getFirstEmail(individual.personalEmail) || 'Aucun email'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => addRecipient(individual, 'to')}
                            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            À
                          </button>
                          <button
                            onClick={() => addRecipient(individual, 'cc')}
                            className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                          >
                            CC
                          </button>
                          <button
                            onClick={() => addRecipient(individual, 'bcc')}
                            className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800"
                          >
                            CCI
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Liste des destinataires */}
            {recipientList.length > 0 && (
              <div className="flex gap-2 items-center mb-2">
                <label className="block font-medium  text-sm">À ({recipientList.length})</label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {recipientList.map(recipient => (
                    <RecipientRow key={recipient.individualId} recipient={recipient} type="to" />
                  ))}
                </div>
              </div>
            )}

            {/* Liste CC */}
            {ccList.length > 0 && (
              <div className="flex gap-2 items-center mb-2">
                <label className="block font-medium  text-sm">CC ({ccList.length})</label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {ccList.map(recipient => (
                    <RecipientRow key={recipient.individualId} recipient={recipient} type="cc" />
                  ))}
                </div>
              </div>
            )}

            {/* Liste CCI */}
            {bccList.length > 0 && (
              <div className="flex gap-2 items-center mb-2">
                <label className="block font-medium  text-sm">CCI ({bccList.length})</label>
                <div className="flex flex-wrap gap-2  max-h-40 overflow-y-auto">
                  {bccList.map(recipient => (
                    <RecipientRow key={recipient.individualId} recipient={recipient} type="bcc" />
                  ))}
                </div>
              </div>
            )}

            {/* Sujet */}
            <div className="mb-2">
              <label className="block font-medium text-sm">Sujet</label>
              <input
                type="text"
                className="w-full p-2 border border-slate-300 rounded-md"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* Message */}
            <div className="mb-2">
              <label className="block font-medium text-sm mb-1">Message</label>
              <div className="border rounded-lg">
                <RichTextEditor
                ref={quillRef}
                value={message}
                onChange={setMessage}
                placeholder="Écrivez votre message..."
              />
              </div>
              

              {/* Barre d'actions sous le text area */}
              <div className="flex items-center gap-2 mt-3 pb-3 border-b border-gray-200">
                {/* Bouton Template */}
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-indigo-400 transition-all"
                >
                  <i className="bi bi-file-text text-indigo-600"></i>
                  <span>Template</span>
                </button>

                {/* Bouton Champs dynamiques */}
                <div className="relative dynamic-fields-dropdown">
                  <button
                    type="button"
                    onClick={() => setShowFieldsDropdown(!showFieldsDropdown)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-indigo-400 transition-all"
                  >
                    <i className="bi bi-code-square text-indigo-600"></i>
                    <span>Champs</span>
                    <i className={`bi bi-chevron-${showFieldsDropdown ? 'up' : 'down'} text-xs`}></i>
                  </button>

                  {/* Dropdown des champs dynamiques */}
                  {showFieldsDropdown && (
                    <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-[250px]">
                      <div className="p-2">
                        <p className="text-xs text-gray-500 px-2 py-1">Insérer un champ personnalisé :</p>
                        {dynamicFields.map((field) => (
                          <button
                            key={field.key}
                            type="button"
                            onClick={() => insertDynamicField(field.key)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 rounded transition-colors"
                          >
                            <div className="font-medium text-gray-900">{field.label}</div>
                            <div className="text-xs text-gray-500">{field.description}</div>
                            <div className="text-xs text-indigo-600 mt-1 font-mono">{`{{${field.key}}}`}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bouton Pièces jointes */}
                <button
                  type="button"
                  onClick={() => document.getElementById('file-upload').click()}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-indigo-400 transition-all"
                >
                  <i className="bi bi-paperclip text-indigo-600"></i>
                  <span>Pièce jointe</span>
                  {attachments.length > 0 && (
                    <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
                      {attachments.length}
                    </span>
                  )}
                </button>

                {/* Affichage des fichiers attachés */}
                {attachments.length > 0 && (
                  <div className="flex-1 flex items-center gap-2 overflow-x-auto">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs whitespace-nowrap">
                        <i className="bi bi-file-earmark"></i>
                        <span>{file.name}</span>
                        <button
                          onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                          className="hover:text-indigo-900"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Input caché pour les fichiers */}
              <input
                id="file-upload"
                type="file"
                multiple
                onChange={(e) => setAttachments(prev => [...prev, ...Array.from(e.target.files)])}
                style={{ display: 'none' }}
              />
            </div>


            {/* Boutons d'action */}
            <div className="mb-2 flex justify-between">
              <button
                onClick={onClose}
                className="bg-slate-300 text-slate-700 px-4 py-2 rounded-md hover:bg-slate-400"
              >
                Annuler
              </button>
              <button
                onClick={handleSendEmail}
                className="bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600"
                disabled={loading}
              >
                {loading ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de sélection de template */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={() => setShowTemplateModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-3 border-b border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900 m-0">Sélectionner un template</h3>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Barre de recherche */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Rechercher un template..."
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Filtres par tags */}
              {allTags.length > 0 && (
                <div>
                  <p className="text-xs text-gray-600 mb-2">Filtrer par tags :</p>
                  <div className="flex flex-wrap gap-2">
                    {allTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => toggleTagFilter(tag)}
                        className={`px-2 py-1 rounded-full text-xs transition-colors ${
                          selectedTagsFilter.includes(tag)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Liste des templates */}
            <div className="overflow-y-auto max-h-[calc(80vh-220px)] p-4">
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <i className="bi bi-file-text text-gray-300 text-5xl mb-3"></i>
                  <p className="text-gray-500">Aucun template disponible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTemplates.map((template) => {
                    const isExpanded = expandedTemplateId === template._id;
                    return (
                      <div
                        key={template._id}
                        className="border rounded-lg border-gray-200 hover:bg-gray-50 cursor-pointer transition-all"
                        onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedTemplateId(isExpanded ? null : template._id);
                                }}
                      >
                        <div className="px-3 py-2 space-y-2">
                          {/* Header avec nom et sujet */}
                          <div className="flex items-start ">
                            <h4 className=" text-gray-900 text-sm m-0">
                              {template.name}
                            </h4>
                          </div>

                          <div className="flex items-center">
                              <p className="text-xs  text-indigo-700   whitespace-nowrap">
                              <p>Objet : {template.subject}</p>
                            </p>
                          </div>

                          {/* Tags */}
                          {template.tags && template.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {template.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Contenu preview ou complet */}
                          <div
                            className={`text-sm text-gray-600 mb-3 ${!isExpanded ? 'line-clamp-2' : ''}`}
                            dangerouslySetInnerHTML={{
                              __html: isExpanded ? template.body : (
                                template.body.length > 150
                                  ? template.body.substring(0, 150) + '...'
                                  : template.body
                              )
                            }}
                          />

                          {/* Footer avec créateur, date et actions */}
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-4">
                              {/* Créateur */}
                              <div className="flex items-center gap-2">
                                {template.user?.photo ? (
                                  <img
                                    src={template.user.photo}
                                    alt={template.user.name || 'User'}
                                    className="w-6 h-6 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-medium">
                                    {template.user?.name?.charAt(0).toUpperCase() || 'U'}
                                  </div>
                                )}
                                <span className="text-xs text-gray-600">{template.user?.name || 'Inconnu'}</span>
                              </div>

                              {/* Date */}
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <i className="bi bi-calendar3"></i>
                                <span>{formatDate(template.createdAt)}</span>
                              </div>
                            </div>

                            {/* Boutons d'actions */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedTemplateId(isExpanded ? null : template._id);
                                }}
                                className="text-gray-500 hover:text-indigo-600 transition-colors p-1"
                                title={isExpanded ? "Réduire" : "Voir en entier"}
                              >
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                              </button>
                              <button
                                onClick={() => handleTemplateChange(template._id)}
                                className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                              >
                                Utiliser
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6  border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendEmailModal;
